import React, { useCallback, useMemo, useState } from 'react';
import { Col, Drawer, Form, Input, Row, Typography } from 'antd';
import { debounce } from 'lodash';
import styled, { useTheme } from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { IScopedSession } from '../../../tracing/session';
import { useAmplitudePassthrough } from '../../../logic/Utility/useAmplitude';
import { EventAcreProposalNames } from '../../../tracing/EventNames';
import { updateLocalPositioning } from '../../../logic/store/Plans/ProductGamePlanSliceV7';
import { useAppDispatch, useAppSelector } from '../../../logic/store/Store';
import { SeriesLookup } from '../../../logic/store/Seeds/SeedLookups';
import { UserPermission } from '../../../logic/Models/Responses/UserPermissions';
import { hasEffectivePermission } from '../../../logic/store/User/AuthSlice';
import { soyId } from '../../../logic/store/Seeds/CropsSlice';

interface ILocalPositioningDrawProps
{
	series: SeriesLookup[0] | undefined;
	session: IScopedSession;
	visible: boolean;

	onClose: () => void;
}

/**
 * This is a slide-out drawer that will let the user edit the local positioning for a series 
 * ( applied across all hybrids at once )
 */
export function LocalPositioningDrawer(props: ILocalPositioningDrawProps)
{
	const {
		series,
		session,
		visible,
		onClose,
	} = props;

	const theme = useTheme();
	const dispatch = useAppDispatch();

	// If it is an existing (saved) plan, use the selectedPlanId, otherwise look for the existing new plan and use that one's planId
	const planId = useAppSelector(state => state.productGamePlanV7.selectedPlanId
		? state.productGamePlanV7.selectedPlanId
		: Object.values(state.productGamePlanV7.plans).find(p => p.isNewPlan)?.planId);
	const plan = useAppSelector(state => planId ? state.productGamePlanV7?.plans[planId] : undefined);
	const userIsGrower = useAppSelector(state => hasEffectivePermission(state, UserPermission.IsGrower, true));
	const customTreatments = useAppSelector(state => state.customTreatments.treatments);
	
	const planProducts = useMemo(() =>
	{
		if (plan && plan.edits && plan.edits.products)
		{
			const filteredProducts = Object.values(plan.edits.products).filter(p => p.seriesId === series?.SeriesId);
			return filteredProducts;
		}

		return [];
	},[plan, plan?.edits, series]);

	const [localPositioning, setLocalPositioning] = useState<string | undefined>(planProducts[0] ? planProducts[0].localPositioning : '');

	const commitChangedText = useAmplitudePassthrough(session, EventAcreProposalNames.UpdateLocalPositioningText, debounce((text) => 
	{
		dispatch(updateLocalPositioning({ planId, seriesId: series?.SeriesId, localPositioningStatement: text }));
	}, 500),
	[planId, series]);

	const applyChangedText = useCallback((text) => 
	{
		setLocalPositioning(text);
		commitChangedText(text);
	}, [setLocalPositioning]);

	return <Drawer
		title="Local Positioning Notes"
		open={visible}
		onClose={onClose}
		destroyOnClose={true}
		width={700}
	>
		<Form layout='vertical'>
			<Row>
				<Col span={24}>
					<ReadOnlySeriesProductRow series={series} />
				</Col>
			</Row>
			<Row>
				<Col span={24} style={{ paddingTop: 32 }}>
					<label htmlFor='localPositioning' style={{
						fontSize: theme.fontSizes.large,
						fontWeight: theme.fontWeights.bold,
						fontFamily: theme.fonts.heading
					}}>
						Local Positioning
					</label>
				</Col>
			</Row>
			<Row>
				<Col span={24}>
					{
						userIsGrower ?
							<div className='localPositioning'>{localPositioning}</div>
							:
							<Input.TextArea
								rows={5}
								showCount
								maxLength={450}
								name='localPositioning'
								value={localPositioning}
								onChange={(evt) => applyChangedText(evt.currentTarget.value)}
							/>
					}
				</Col>
			</Row>
			{/* We only want custom treatments that are not deleted, custom treatments currently only belong to the soybean crop */}
			{customTreatments?.filter(ct => !ct.IsDeleted && series?.CropId === soyId).map(customTreatment =>
			{
				const { Name, Notes, Id } = customTreatment;
				const key = `customTreatments-${Id}`;
				return <div key={[key, `-${props.series?.SeriesId}`].join('')}>
					<Row >
						<Col span={24} style={{ paddingTop: 12 }}>
							<label htmlFor={key} style={{
								fontSize: theme.fontSizes.large,
								fontWeight: theme.fontWeights.bold,
								fontFamily: theme.fonts.heading
							}}>
								{Name}
							</label>
						</Col>
					</Row>
					<Row>
						<Col span={24} className={key}>
							<Typography.Text>{Notes}</Typography.Text>
						</Col>
					</Row>
				</div>;
			})}
		</Form>

	</Drawer>;
}

const StyledSeriesRow = styled.div`
	display: flex;
	flex-direction: row;
	width: 100%;
	height: 50px;
	min-height: 50px;
	border: 1px solid;
	border-color: ${themeGet('colors.lightestGrey')};
	border-top-left-radius: 5px;
	border-top-right-radius: 5px;
	margin-top: 16px;
	align-items: center;

	.SeriesName { 
		color: ${themeGet('colors.darkGrey')};
		font-family: ${themeGet('fonts.base')};
		font-size: ${themeGet('fontSizes.extraSmall')};
		font-weight: ${themeGet('fontWeights.bold')};
		padding-left: 12px;
		padding-right: 12px;
		width: 88px;
	}

	.SeriesRm {
		color: ${themeGet('colors.darkGrey')};
		font-family: ${themeGet('fonts.base')};
		font-size: ${themeGet('fontSizes.extraSmall')};
		font-weight: ${themeGet('fontWeights.bold')};
		padding-left: 12px;
		padding-right: 12px;
	}

	.Spacer {
		flex-grow: 1;
	}

	.SeriesAction {
		padding-right: 12px;
		display: flex;
		flex-direction: row;
	}
`;

const ReadOnlySeriesProductRow = (props: { series: SeriesLookup[0] | undefined }) =>
{
	const { series } = props;
	return <StyledSeriesRow className={`SeriesContainer SeriesContainer-${series?.SeriesId}`}>
		<div className='SeriesAvailability' />
		<div className='SeriesName'>{series?.SeriesName}</div>
		<div className='SeriesRm'>RM: {series?.RelativeMaturity}</div>
		<div className='Spacer' />
	</StyledSeriesRow>;
};